import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useAuthStore } from './stores/authStore'
import { useChatStore } from './stores/chatStore'
import LoginPage from './pages/LoginPage'
import OnboardingPage from './pages/OnboardingPage'
import LibraryPage from './pages/LibraryPage'
import WelcomePage from './pages/WelcomePage'
import SettingsPage from './pages/SettingsPage'
import DocumentationPage from './pages/DocumentationPage'
import InvitePage from './pages/InvitePage'
import KnowledgeGraphPage from './pages/KnowledgeGraphPage'
import IntegrationsPage from './pages/IntegrationsPage'
import SpacesPage from './pages/SpacesPage'
import LearningPathsPage from './pages/LearningPathsPage'
import LearningPathDetailPage from './pages/LearningPathDetailPage'
import OAuthTestPage from './pages/OAuthTestPage'
import Layout from './components/Layout'

function App() {
  const { isAuthenticated, user, logout } = useAuthStore()
  const resetChat = useChatStore(state => state.reset)
  const navigate = useNavigate()

  // Listen for logout from extension (via storage change)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'kuraAuthToken' && e.oldValue && !e.newValue) {
        console.log('Auth token removed from localStorage, logging out...')
        logout()
        navigate('/login')
      }
      if (e.key === 'kura-auth' && e.oldValue && !e.newValue) {
        console.log('Zustand auth state removed, logging out...')
        logout()
        navigate('/login')
      }
    }
    
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [logout, navigate])

  // Listen for logout from extension (via custom event from contentScript)
  useEffect(() => {
    let isHandlingLogout = false
    
    const handleExtensionAuth = async (e: Event) => {
      const customEvent = e as CustomEvent
      const { token } = customEvent.detail || {}
      if (!token && isAuthenticated && !isHandlingLogout) {
        isHandlingLogout = true
        console.log('[App] Extension triggered logout via kuraAuthUpdate')
        await logout()
        navigate('/login')
        // Reset flag after a delay
        setTimeout(() => { isHandlingLogout = false }, 1000)
      }
    }
    
    window.addEventListener('kuraAuthUpdate', handleExtensionAuth)
    return () => window.removeEventListener('kuraAuthUpdate', handleExtensionAuth)
  }, [logout, navigate, isAuthenticated])

  // Clear chat history if user changes
  useEffect(() => {
    const checkUserChange = () => {
      try {
        const storedUser = localStorage.getItem('kuraUser')
        if (storedUser && user) {
          const parsedUser = JSON.parse(storedUser)
          if (parsedUser.id !== user.id) {
            console.log('User ID mismatch detected, clearing chat history')
            resetChat()
            localStorage.removeItem('Memtrail-chat')
          }
        }
      } catch (error) {
        console.error('Error checking user change:', error)
      }
    }
    checkUserChange()
  }, [user, resetChat])

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<LoginPage />} />
      <Route path="/welcome" element={<WelcomePage />} />
      <Route path="/oauth-test" element={<OAuthTestPage />} />
      
      {/* Onboarding route */}
      <Route path="/onboarding" element={
        isAuthenticated ? <OnboardingPage /> : <Navigate to="/login" replace />
      } />
      
      {/* Protected routes */}
      <Route path="/" element={
        isAuthenticated ? <Navigate to="/library" replace /> : <Navigate to="/login" replace />
      } />
      
      <Route path="/library" element={
        isAuthenticated ? (
          <Layout>
            <LibraryPage />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      
      <Route path="/settings" element={
        isAuthenticated ? (
          <Layout>
            <SettingsPage />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      
      <Route path="/knowledge-graph" element={
        isAuthenticated ? (
          <Layout>
            <KnowledgeGraphPage />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      
      <Route path="/spaces" element={
        isAuthenticated ? (
          <Layout>
            <SpacesPage />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      
      <Route path="/integrations" element={
        isAuthenticated ? (
          <Layout>
            <IntegrationsPage />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      
      <Route path="/learning-paths" element={
        isAuthenticated ? (
          <Layout>
            <LearningPathsPage />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      
      <Route path="/learning-paths/:pathId" element={
        isAuthenticated ? (
          <Layout>
            <LearningPathDetailPage />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      
      <Route path="/documentation" element={
        isAuthenticated ? (
          <Layout>
            <DocumentationPage />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      
      <Route path="/invite" element={
        isAuthenticated ? (
          <Layout>
            <InvitePage />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App